/**
 * Pure formatting helpers for the per-step tooltip in WorkflowPhasePipeline.
 *
 * Lives in the shared module so both ora and the Outlook add-in can render
 * identical tooltip content. The shared component exposes a render-slot
 * prop that lets each app wrap the trigger with its own tooltip primitive
 * (ora uses CustomTooltip / MUI; the add-in can use plain HTML); the
 * formatted *parts* stay the same.
 *
 * Import alias in ora:     ~/common/workflows/stepTooltipParts
 * Import alias in add-in:  @product-app/common/workflows/stepTooltipParts
 */

import { decomposeDuration } from "./formatTime";
import type { PhaseDefinition, PhaseStatus } from "./types";
import type { PipelineHitlEvent } from "./components/WorkflowPhasePipeline";

export interface StepTooltipInfo {
  phase: PhaseDefinition;
  state: PhaseStatus["state"];
  startedAt?: string | null;
  endedAt?: string | null;
  errorMessage?: string | null;
  /** 0-based step index within the workflow manifest. */
  index: number;
  /** Total number of steps. */
  total: number;
  hitlEvent?: PipelineHitlEvent | null;
}

/**
 * One row in the formatted tooltip. `value` is null when the underlying
 * data isn't available (e.g. timestamps for instances that pre-date the
 * substrate's phase_history migration) — callers should drop those rows.
 */
export interface StepTooltipRow {
  label: string;
  value: string | null;
}

/** Compact `HH:MM:SS` for same-day events; `MMM D, HH:MM` otherwise. */
export function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return d.toLocaleString(undefined, {
    month: sameDay ? undefined : "short",
    day: sameDay ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * The tooltip's OWN template around the shared decomposition. It differs from
 * `formatTime`'s `formatDuration` in exactly one place — a sub-second phase
 * reads "0s" here rather than "<1s", because a tooltip row labelled "Duration"
 * is reporting a measurement, not summarising a run. The h/m/s arithmetic
 * itself is not restated: it lives once, in `decomposeDuration`.
 */
function renderDuration(ms: number): string {
  const { totalSeconds, totalMinutes, hours, minutes, seconds } =
    decomposeDuration(ms);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  if (totalMinutes < 60)
    return seconds ? `${totalMinutes}m ${seconds}s` : `${totalMinutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/** "1m 23s" / "12s" / "2h 4m" — null when either timestamp is missing. */
export function formatDuration(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string | null {
  if (!startIso || !endIso) return null;
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return renderDuration(ms);
}

/** Elapsed-since-start when no end timestamp — for currently-running phases. */
export function formatElapsedSince(
  startIso: string | null | undefined,
  nowOverride?: Date,
): string | null {
  if (!startIso) return null;
  const now = (nowOverride ?? new Date()).getTime();
  const ms = now - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return renderDuration(ms);
}

const KIND_LABELS: Record<string, string> = {
  python: "Automated (Python)",
  rpa: "Browser automation (RPA)",
  hitl: "Human review",
  degraded: "Degraded fallback",
};

export function formatKind(type: string | undefined | null): string {
  if (!type) return "—";
  return KIND_LABELS[type] ?? type;
}

const STATE_EXPLANATIONS: Record<PhaseStatus["state"], string> = {
  pending: "Not yet started",
  running: "In progress",
  completed: "Finished successfully",
  failed: "Errored — see message below",
  hitl_waiting: "Waiting for human input",
  skipped: "Skipped (prior step failed or cancelled)",
};

export function explainState(state: PhaseStatus["state"]): string {
  return STATE_EXPLANATIONS[state];
}

/**
 * Build the ordered list of rows the tooltip should render. Callers drop
 * rows whose `value` is null. Keeps the rendering layer trivial — the
 * tooltip implementation just maps over the rows it gets.
 *
 * The ordering here is deliberate: the user's most-frequent question
 * ("when did this start, how long has it been running") goes first;
 * descriptive metadata (kind, position, ID) follows; failure / HITL
 * specifics last so they don't push timing out of view when present.
 */
export function buildTooltipRows(info: StepTooltipInfo): StepTooltipRow[] {
  const rows: StepTooltipRow[] = [];

  const startedFmt = formatTimestamp(info.startedAt);
  const endedFmt = formatTimestamp(info.endedAt);
  const duration = formatDuration(info.startedAt, info.endedAt);
  const elapsed =
    info.state === "running" || info.state === "hitl_waiting"
      ? formatElapsedSince(info.startedAt)
      : null;

  if (startedFmt) rows.push({ label: "Started", value: startedFmt });
  if (endedFmt) rows.push({ label: "Ended", value: endedFmt });
  if (duration) rows.push({ label: "Duration", value: duration });
  else if (elapsed) rows.push({ label: "Running for", value: elapsed });

  rows.push({ label: "Kind", value: formatKind(info.phase.type) });
  rows.push({ label: "State", value: explainState(info.state) });
  rows.push({
    label: "Position",
    value: `Step ${info.index + 1} of ${info.total}`,
  });
  rows.push({ label: "Step ID", value: info.phase.id });

  if (info.state === "failed" && info.errorMessage) {
    rows.push({ label: "Error", value: info.errorMessage });
  }

  if (info.hitlEvent) {
    const e = info.hitlEvent;
    const askedFmt = formatTimestamp(e.asked_at);
    const answeredFmt = formatTimestamp(e.answered_at);
    const latency = formatDuration(e.asked_at, e.answered_at);
    if (askedFmt) rows.push({ label: "Asked", value: askedFmt });
    if (answeredFmt) rows.push({ label: "Answered", value: answeredFmt });
    if (latency) rows.push({ label: "Decision time", value: latency });
  }

  return rows.filter((r) => r.value !== null && r.value !== "");
}
